// klaviyo-bridge.js
// Maps the Lumi profile to the EXISTING Lagoon Klaviyo schema (so Lumi
// profiles slot into the same cohorts/flows the current pillow quiz feeds).
//
// Schema kept identical to the quiz:
//   wake_up_position   single  "Side" | "Back" | "Stomach" | "Changes all the time"
//   density            single  "Soft & Cloud-like" | "Cushy & Cradling" | "Firm & Supportive" | "Not sure"
//   body_type          single  "Slender" | "Average" | "Broad"
//   height             single  "Lower loft" | "Medium loft" | "Higher loft" | "I'm not sure"
//   sleep_issues       multi   comma-joined display strings (see ISSUE_LABELS)
//   sleep_goals        multi   comma-joined display strings (see GOAL_LABELS)
//   result1_name       single  "The Otter" etc.
//   Pillowcase         single  "Cooling Flex" | "Silk Luxe" (when complement was accepted)
//   Entered Welcome Flow  "Yes"
//   Accepts Marketing  true
//
// On submit we also subscribe the profile to the "Cartful Solutions" list so
// the existing lifecycle flows fire. Set window.LAGOON_KLAVIYO_LIST_ID to
// the Cartful Solutions list ID.
//
// PRODUCTION WIRING:
//   window.LAGOON_KLAVIYO_PUBLIC_KEY = '<6-char site ID>';
//   window.LAGOON_KLAVIYO_LIST_ID    = '<Cartful Solutions list ID>';
//   // optional — preferred over public-key for production:
//   window.LAGOON_KLAVIYO_PROXY = 'https://your.backend/klaviyo-identify';
//
// LOCAL STORAGE KEYS:
//   lagoon.lumi.session.v1    full conversation + profile + recommendation
//   lagoon.lumi.email.v1      submitted email (so we don't re-prompt)
//   lagoon.lumi.synced.v1     {syncedAt, props, pending} sync receipt

window.LAGOON_KLAVIYO_PUBLIC_KEY = window.LAGOON_KLAVIYO_PUBLIC_KEY || null;
window.LAGOON_KLAVIYO_PROXY      = window.LAGOON_KLAVIYO_PROXY      || null;
window.LAGOON_KLAVIYO_LIST_ID    = window.LAGOON_KLAVIYO_LIST_ID    || null;
// The metric your welcome-series flow triggers on. MUST match the legacy
// quiz's event name EXACTLY (case-sensitive) or the flow won't fire.
window.LAGOON_KLAVIYO_SURVEY_METRIC = window.LAGOON_KLAVIYO_SURVEY_METRIC || 'Filled Out Survey';

// ─────────────────────────────────────────────────────────────
// Display-label maps — match the existing quiz's Klaviyo values exactly.
// ─────────────────────────────────────────────────────────────

const POSITION_LABELS = {
  side: 'Side',
  back: 'Back',
  stomach: 'Stomach',
  combo: 'Changes all the time',
};

const DENSITY_LABELS = {
  soft: 'Soft & Cloud-like',
  cushy: 'Cushy & Cradling',
  firm: 'Firm & Supportive',
  notsure: 'Not sure',
};

const BODY_LABELS = {
  slender: 'Slender',
  average: 'Average',
  broad: 'Broad',
};

const HEIGHT_LABELS = {
  low: 'Lower loft',
  medium: 'Medium loft',
  high: 'Higher loft',
  notsure: "I'm not sure",
};

const ISSUE_LABELS = {
  toss:     'I toss & turn too much',
  neck:     'I wake up with neck or shoulder pain',
  hot:      'I sleep hot & need cooling',
  snore:    'I snore or have trouble breathing',
  wrinkles: 'I worry about face wrinkles & pressure',
  none:     'No issues, just looking for an upgrade',
};

const GOAL_LABELS = {
  fall_asleep_faster: 'Fall asleep faster',
  deep_sleep:         'Get more deep & REM sleep',
  pain_free:          'Wake up pain-free',
  cool:               'Stay cool all night',
  upgrading:          'Just upgrading',
};

const PILLOW_LABELS = {
  otter:      'The Otter',
  otter2:     'The Otter II',   // NOTE: any Klaviyo flow/segment filtering
                                // result1_name must use "contains The Otter"
                                // rather than "equals" to catch both.
  fox:        'The Fox',
  chinchilla: 'The Chinchilla',
  puffin:     'The Puffin',
  polarbear:  'The Polar Bear',
};

const PILLOWCASE_LABELS = {
  'cooling-pillowcase': 'Cooling Flex',
  'silk-pillowcase':    'Silk Luxe',
};

// ── kiosk mode (shared booth iPad) ─────────────────────────────────────────
// One device, many different visitors, back to back. Everything this bridge
// normally persists to speed up a RETURNING visitor becomes a liability in
// that setting: the next person would inherit the previous person's email,
// and their onsite events would land on the previous person's Klaviyo profile.
//
// Turn it on once per device by visiting any page with ?kiosk=1 — it sticks in
// localStorage, so it survives the /discount/ redirect and every page after.
// ?kiosk=0 turns it off. Expo attribution is untouched: on a booth iPad every
// session is expo traffic by definition.
var KIOSK_KEY = 'lagoon_kiosk_mode';

(function initKiosk() {
  try {
    var v = new URLSearchParams(location.search).get('kiosk');
    if (v === '1' || v === 'true')  localStorage.setItem(KIOSK_KEY, '1');
    if (v === '0' || v === 'false') localStorage.removeItem(KIOSK_KEY);
  } catch (e) {}
})();

function isKiosk() {
  if (window.LAGOON_KIOSK === true) return true;
  try { return localStorage.getItem(KIOSK_KEY) === '1'; } catch (e) { return false; }
}
window.lagoonIsKiosk = isKiosk;

// Ends the current visitor's session on this device: forget the email, forget
// the sync record, and drop Klaviyo's onsite identity so the NEXT visitor's
// events are not attributed to this profile. Deliberately does NOT clear the
// expo marker.
// opts.keepPersonalization — leave the CURRENT visitor's match band and offer
// bar in place. Set by the automatic post-capture reset, which fires the
// moment their email lands, while they are still shopping their own result.
// A staff reset between visitors passes nothing and clears everything.
window.lagoonKioskReset = function (opts) {
  try {
    localStorage.removeItem('lagoon.lumi.email.v1');
    localStorage.removeItem('lagoon.lumi.synced.v1');
  } catch (e) {}
  // The personalized match band and active-offer bar render on default PDPs
  // and the cart from these keys. Left behind, the next visitor sees the
  // previous person's recommended pillow and confidence score as their own —
  // the most customer-visible contamination on a shared device. Each visitor's
  // own quiz rewrites them, so clearing costs nothing.
  if (!(opts && opts.keepPersonalization)) {
    try {
      localStorage.removeItem('lagoon_match');
      localStorage.removeItem('lagoon_offer');
    } catch (e) {}
  }
  // Klaviyo's onsite identity. Expired across every host and path variant the
  // cookie may carry, since a mismatched write silently no-ops.
  //
  // NOT guaranteed: Klaviyo's script also holds identity in memory for the
  // life of the page, and may set the cookie with attributes these writes
  // cannot match. Verify on the real iPad (Section 10) rather than assuming;
  // if __kla_id survives, staff need a hard reload between visitors.
  try {
    var host = location.hostname;
    var bare = host.replace(/^www\./, '');
    var domains = ['', '; domain=' + host, '; domain=.' + host, '; domain=' + bare, '; domain=.' + bare];
    var paths = ['/', ''];
    domains.forEach(function (d) {
      paths.forEach(function (pa) {
        document.cookie = '__kla_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT' +
          (pa ? '; path=' + pa : '') + d;
      });
    });
    if (window.klaviyo && typeof window.klaviyo.reset === 'function') window.klaviyo.reset();
  } catch (e) {}
  // Report whether the cookie actually went, so the caller / booth staff can
  // tell a real reset from a silent no-op.
  var cleared = true;
  try { cleared = document.cookie.indexOf('__kla_id') === -1; } catch (e) {}
  if (!cleared) console.warn('[kiosk] __kla_id survived the reset — hard-reload the iPad between visitors.');
  return { emailCleared: true, klaviyoIdentityCleared: cleared };
};

// ── expo attribution ───────────────────────────────────────────────────────
// One property: expo_attendee.
//
// The printed QR codes point at /discount/EXPO?redirect=..., which is a server
// redirect — no page is rendered there, so no script of ours ever sees that
// URL. Attribution therefore can't rely on the address bar, and instead reads
// any of the traces the discount link leaves behind. Checked in order, and a
// hit on ANY of them is enough:
//
//   1. Shopify's discount_code cookie, set by the /discount/ link itself.
//      Independent of the cart, so it works on a first-page bounce.
//   2. The codes Shopify reports on the cart at page render, exposed by
//      expo-gift.liquid. Includes superseded codes.
//   3. The flag expo-gift.liquid writes when EXPO is genuinely in effect.
//   4. The word "expo" anywhere in the URL — path or query, any case. Covers
//      future links and a dedicated /pages/expo-* landing.
//
// Attribution is a one-way latch: once stamped, later signals can't unstamp
// it. That's safe here precisely because it's a marketing fact ("this visitor
// came from the expo") rather than an entitlement — unlike the gift gate,
// which must read live state every time.
var EXPO_ATTR_KEY = 'lagoon_expo_attr';
var EXPO_ATTR_DAYS = 14;
var EXPO_CODE = 'EXPO';

function expoSignalPresent() {
  try {
    var m = document.cookie.match(/(?:^|;\s*)discount_code=([^;]*)/);
    if (m && decodeURIComponent(m[1]).toUpperCase().indexOf(EXPO_CODE) !== -1) return true;
  } catch (e) {}
  try {
    var cfg = window.EXPO_GIFT_CONFIG;
    if (cfg) {
      var lists = [cfg.codesSeenAtRender, cfg.codesAtRender];
      for (var i = 0; i < lists.length; i++) {
        var l = lists[i];
        if (l && l.length) {
          for (var j = 0; j < l.length; j++) {
            if (String(l[j]).toUpperCase() === EXPO_CODE) return true;
          }
        }
      }
    }
  } catch (e) {}
  try { if (localStorage.getItem('lagoon_expo_intent')) return true; } catch (e) {}
  try { if (/expo/i.test(location.href)) return true; } catch (e) {}
  return false;
}

// Re-checked on every call, not just at load: the bridge may execute before
// expo-gift.liquid has defined its config, and the quiz submit happens long
// after either. Persisted because the submit is usually several pages after
// the scan, and window-limited so a device that attended in March isn't still
// tagged as expo traffic in July.
function readExpoAttribution() {
  var stored = false;
  try {
    var ts = parseInt(localStorage.getItem(EXPO_ATTR_KEY), 10);
    if (ts && (Date.now() - ts) < EXPO_ATTR_DAYS * 864e5) stored = true;
    else if (ts) localStorage.removeItem(EXPO_ATTR_KEY);
  } catch (e) {}
  if (stored) return true;
  if (expoSignalPresent()) {
    try { localStorage.setItem(EXPO_ATTR_KEY, String(Date.now())); } catch (e) {}
    return true;
  }
  return false;
}

// Bank the signal at load even if the visitor never submits.
readExpoAttribution();
window.lagoonExpoAttribution = readExpoAttribution;

// Build the Klaviyo property payload from a Lumi profile + recommendation.
// Skips empty values so we don't blow away existing properties with blanks.
function mapProfileToKlaviyo(p, recommendation, email) {
  // sleep_issues is multi-select. Internal model uses `concerns` (array) OR
  // `concern` (legacy single). Also: temp=hot maps into sleep_issues since
  // the existing schema doesn't have a separate temp field — that's how
  // hot-sleeper segmentation works today.
  const issues = new Set();
  if (Array.isArray(p.concerns)) p.concerns.forEach(c => ISSUE_LABELS[c] && issues.add(ISSUE_LABELS[c]));
  if (p.concern && ISSUE_LABELS[p.concern]) issues.add(ISSUE_LABELS[p.concern]);
  if (p.temp === 'hot' && !issues.has(ISSUE_LABELS.hot)) issues.add(ISSUE_LABELS.hot);

  // sleep_goals — already an array in the Lumi model
  const goals = Array.isArray(p.goals)
    ? p.goals.map(g => GOAL_LABELS[g]).filter(Boolean)
    : [];

  const out = {
    // Klaviyo reserved
    $email: email || p.email,
    $consent: ['email'],
    'Accepts Marketing': true,

    // EXISTING quiz schema — same keys and value formats
    wake_up_position: POSITION_LABELS[p.position] || null,
    density:          DENSITY_LABELS[p.density]   || null,
    body_type:        BODY_LABELS[p.body]         || null,
    height:           HEIGHT_LABELS[p.height]     || null,
    sleep_issues:     issues.size ? [...issues].join(',') : null,
    sleep_goals:      goals.length ? goals.join(',') : null,
    result1_name:     PILLOW_LABELS[recommendation?.pillow_id] || null,
    Pillowcase:       PILLOWCASE_LABELS[recommendation?.complement?.id] || null,
    'Entered Welcome Flow': 'Yes',

    // Lumi-specific properties — useful but namespaced so they don't
    // collide with existing reporting. Optional to consume.
    lumi_runner_up:              PILLOW_LABELS[recommendation?.runner_up_id] || null,
    lumi_recommended_confidence: recommendation?.confidence || null,
    lumi_buying_for:             p.buying_for || null,
    lumi_medical_flag:           p.medical_flag === true ? true : null,
    lumi_source:                 'lagoon-sleep-concierge',
    lumi_session_completed_at:   new Date().toISOString(),
  };

  // Expo attribution — present only for expo traffic, so its absence is
  // itself the "online" case.
  if (readExpoAttribution()) out.expo_attendee = 'Yes';

  // Strip empty/null so Klaviyo doesn't clear existing values
  Object.keys(out).forEach(k => {
    const v = out[k];
    if (v === null || v === undefined || v === '') delete out[k];
  });
  return out;
}

// Submit the profile to Klaviyo + subscribe to Cartful Solutions list.
// Prefers server proxy when configured; otherwise uses public client API.
window.lagoonSyncToKlaviyo = async function({ email, profile, recommendation }) {
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { ok: false, error: 'invalid-email' };
  }
  const props = mapProfileToKlaviyo(profile || {}, recommendation || {}, email);

  // Persist locally first so a network failure never loses the data.
  // Skipped on a shared device: this receipt exists so a failed push can be
  // retried on a later page load, and on a booth iPad that later page load
  // belongs to a DIFFERENT visitor — retrying would re-send the previous
  // person's email and answers under their session. Losing one failed capture
  // is the lesser harm than attributing it to the wrong person.
  if (!isKiosk()) {
    try {
      localStorage.setItem('lagoon.lumi.email.v1', email);
      localStorage.setItem('lagoon.lumi.synced.v1', JSON.stringify({
        syncedAt: Date.now(), props, pending: true,
      }));
    } catch (e) {}
  }

  // 1) Preferred: server proxy. Your backend uses the Klaviyo private key
  //    to identify + subscribe. Single endpoint, atomic.
  if (window.LAGOON_KLAVIYO_PROXY) {
    try {
      const res = await fetch(window.LAGOON_KLAVIYO_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          properties: props,
          list_id: window.LAGOON_KLAVIYO_LIST_ID,
          // Tell the backend to fire the survey metric that triggers the
          // welcome flow. (Your proxy must emit this as a Klaviyo event.)
          event: window.LAGOON_KLAVIYO_SURVEY_METRIC,
        }),
      });
      if (!res.ok) throw new Error('proxy-' + res.status);
      markSynced(props);
      return { ok: true, via: 'proxy' };
    } catch (e) {
      console.warn('[lumi] proxy failed, falling back to client API', e);
    }
  }

  // 2) Fallback: public client API (no private key needed). Two calls —
  //    update profile properties, then subscribe to the list.
  if (window.LAGOON_KLAVIYO_PUBLIC_KEY) {
    try {
      // Fire all three CONCURRENTLY. The old serial chain meant the survey
      // metric was the last request to start, so a redirect killed it first.
      // Klaviyo creates the profile implicitly on both subscribe and event,
      // so nothing depends on the profile call finishing first.
      const calls = [
        klaviyoUpdateProfile(email, props),
        // The metric that triggers the welcome-series flow. Subscribing to a
        // list / setting a property does NOT start an event-based flow.
        klaviyoTrackEvent(email, window.LAGOON_KLAVIYO_SURVEY_METRIC, props),
      ];
      if (window.LAGOON_KLAVIYO_LIST_ID) {
        calls.push(klaviyoSubscribeToList(email, window.LAGOON_KLAVIYO_LIST_ID));
      }
      const names = window.LAGOON_KLAVIYO_LIST_ID
        ? ['profiles', 'events', 'subscriptions']
        : ['profiles', 'events'];
      const results = await Promise.allSettled(calls);
      const failed = results
        .map((r, i) => ({ r: r, name: names[i] }))
        .filter(x => x.r.status === 'rejected');

      if (failed.length === results.length) {
        klTrack('klaviyo_sync_failed', { legs: names.join(','), reason: String(failed[0].r.reason) });
        throw failed[0].r.reason;
      }
      if (failed.length) {
        // Partial: e.g. the event landed but the list add didn't. Previously a
        // console.warn nobody would ever see.
        klTrack('klaviyo_sync_partial', {
          failed_legs: failed.map(x => x.name).join(','),
          ok_legs: names.filter(n => !failed.some(x => x.name === n)).join(','),
        });
        console.warn('[lagoon] partial Klaviyo sync', failed.map(x => x.name + ': ' + x.r.reason));
      } else {
        // Compare this count to GA4 quiz_email_captured — they should track
        // within a few percent. Sustained divergence = pipeline broken.
        klTrack('klaviyo_sync_ok', { legs: names.join(',') });
      }
      markSynced(props);
      return { ok: true, via: 'client-api' };
    } catch (e) {
      console.error('[lumi] client API failed', e);
      return { ok: false, error: String(e) };
    }
  }

  // 3) Demo mode — log the payload so the UX flow can be reviewed without
  //    live Klaviyo keys. Local storage still has it; production picks up
  //    on next visit when keys are wired.
  console.info('[lumi DEMO] Klaviyo payload:', { email, properties: props, event: window.LAGOON_KLAVIYO_SURVEY_METRIC, list: window.LAGOON_KLAVIYO_LIST_ID || '(not set — would subscribe to Cartful Solutions)' });
  markSynced(props);
  return { ok: true, via: 'demo' };
};

async function klaviyoUpdateProfile(email, properties) {
  const customProps = Object.fromEntries(
    Object.entries(properties).filter(([k]) => !k.startsWith('$') && k !== 'Accepts Marketing')
  );
  const payload = {
    data: {
      type: 'profile',
      attributes: {
        email,
        properties: customProps,
      },
    },
  };
  const url = `https://a.klaviyo.com/client/profiles/?company_id=${window.LAGOON_KLAVIYO_PUBLIC_KEY}`;
  const res = await kFetch(url, payload, 'profiles');
  // 409 is "profile already exists" — fine, properties merge.
  if (!res.ok && res.status !== 409) throw new Error('profile-' + res.status);
}

async function klaviyoSubscribeToList(email, listId) {
  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Lagoon Sleep Concierge',
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: listId } },
      },
    },
  };
  const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${window.LAGOON_KLAVIYO_PUBLIC_KEY}`;
  const res = await kFetch(url, payload, 'subscriptions');
  if (!res.ok && res.status !== 202) throw new Error('subscribe-' + res.status);
}

// Fire a Klaviyo metric/event via the public client API. This is what an
// event-triggered flow (e.g. the welcome series) listens for — a profile
// property or list subscription will NOT start it.
// ── failure telemetry ──────────────────────────────────────────────────────
// The Sep 2026 outage (redirect aborting in-flight fetches) was invisible for
// ~3 months because every failure was swallowed by a catch. Any non-2xx or
// thrown call now emits a GA4/GTM event, so a broken pipeline is visible in a
// day instead of a quarter. Never throws — telemetry must not break the sync.
function klTrack(ev, data) {
  const payload = Object.assign({ source: 'klaviyo_bridge' }, data || {});
  try { if (typeof window.lumiTrack === 'function') window.lumiTrack(ev, payload); } catch (e) {}
  try { if (typeof window.gtag === 'function') window.gtag('event', ev, payload); } catch (e) {}
  try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(Object.assign({ event: ev }, payload)); } catch (e) {}
}
window.lagoonKlaviyoTrack = klTrack;

// All Klaviyo client-API calls go through this. keepalive:true is the critical
// flag: the quiz redirects to the PDP immediately after submit, and without it
// the browser cancels these requests mid-flight — which silently dropped the
// "Filled Out Survey" metric and starved the welcome flow.
function kFetch(url, payload, call, attempt) {
  attempt = attempt || 1;
  const t0 = Date.now();
  // Retry once on 429 (rate limit) or 5xx — transient by definition. Anything
  // else fails fast so real breakage stays loud.
  const retryable = s => s === 429 || (s >= 500 && s < 600);
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).then(function (res) {
    // 409 on /profiles/ means "already exists" — a success for our purposes.
    const ok = res.ok || res.status === 202 || res.status === 409;
    if (!ok) {
      if (attempt === 1 && retryable(res.status)) {
        // Klaviyo returns Retry-After on 429; a fixed delay can retry before
        // the rate-limit window resets. Cap so we never outlive the page.
        const ra = parseInt(res.headers.get('Retry-After'), 10);
        const waitMs = Math.min(isNaN(ra) ? 600 : ra * 1000, 5000);
        klTrack('klaviyo_call_retry', { call: call, status: res.status, wait_ms: waitMs });
        return new Promise(r => setTimeout(r, waitMs))
          .then(() => kFetch(url, payload, call, 2));
      }
      klTrack('klaviyo_call_failed', { call: call, status: res.status, attempt: attempt, ms: Date.now() - t0 });
      console.error('[lagoon] Klaviyo ' + call + ' failed: HTTP ' + res.status);
    }
    return res;
  }).catch(function (err) {
    // This is the branch a cancelled/aborted request lands in — the exact
    // signature of the outage. Surfacing it is the whole point.
    klTrack('klaviyo_call_failed', { call: call, status: 'network', error: String(err && err.message || err), ms: Date.now() - t0 });
    console.error('[lagoon] Klaviyo ' + call + ' network error', err);
    throw err;
  });
}

// Deterministic per logical event: Klaviyo discards duplicate submissions
// sharing unique_id + profile + metric, so a retry can't double-count.
function eventUniqueId(email, metricName) {
  const day = new Date().toISOString().slice(0, 10);
  return [metricName, email, day, Math.floor(Date.now() / 60000)].join('|');
}

async function klaviyoTrackEvent(email, metricName, props) {
  // Keep event properties light + non-reserved (no $email/$consent here).
  const eventProps = Object.fromEntries(
    Object.entries(props || {}).filter(([k]) => !k.startsWith('$') && k !== 'Accepts Marketing')
  );
  const payload = {
    data: {
      type: 'event',
      attributes: {
        unique_id: eventUniqueId(email, metricName),
        properties: eventProps,
        metric: {
          data: { type: 'metric', attributes: { name: metricName } },
        },
        profile: {
          data: { type: 'profile', attributes: { email } },
        },
      },
    },
  };
  const url = `https://a.klaviyo.com/client/events/?company_id=${window.LAGOON_KLAVIYO_PUBLIC_KEY}`;
  const res = await kFetch(url, payload, 'events');
  if (!res.ok && res.status !== 202) throw new Error('event-' + res.status);
}

function markSynced(props) {
  // Kiosk: a completed capture IS the end of that visitor's session, so wipe
  // the identity now rather than waiting for someone to remember to. The
  // Klaviyo write has already happened by this point, so nothing is lost.
  if (isKiosk()) { window.lagoonKioskReset({ keepPersonalization: true }); return; }
  try {
    localStorage.setItem('lagoon.lumi.synced.v1', JSON.stringify({
      syncedAt: Date.now(), props, pending: false,
    }));
  } catch (e) {}
}

window.lagoonGetSavedEmail = function() {
  // Kiosk: always null, so the quiz asks every visitor to type their own
  // address instead of offering the previous visitor's as a one-tap submit.
  // This is the single most important part of kiosk mode.
  if (isKiosk()) return null;
  try { return localStorage.getItem('lagoon.lumi.email.v1') || null; }
  catch (e) { return null; }
};

window.lagoonClearKlaviyoState = function() {
  try {
    localStorage.removeItem('lagoon.lumi.email.v1');
    localStorage.removeItem('lagoon.lumi.synced.v1');
  } catch (e) {}
};

// Expose the label maps so the strategy artboard can render them
window.LAGOON_KLAVIYO_LABELS = {
  position: POSITION_LABELS,
  density:  DENSITY_LABELS,
  body:     BODY_LABELS,
  height:   HEIGHT_LABELS,
  issues:   ISSUE_LABELS,
  goals:    GOAL_LABELS,
  pillow:   PILLOW_LABELS,
  pillowcase: PILLOWCASE_LABELS,
};

// ── onsite (cookie-attributed) tracking ────────────────────────────────────
// Fires a metric for a visitor Klaviyo ALREADY knows, without an email submit.
// This is the mechanism the legacy Cartful quiz used, and the reason its event
// volume (~2,423/mo) was ~7x its email capture (~330/mo).
//
// Uses Klaviyo's onsite JS, which is loaded sitewide by the Shopify
// integration. Supports both the current (window.klaviyo) and legacy
// (_learnq) globals. Returns true if we handed the event off.
window.lagoonTrackOnsite = function (metricName, props) {
  const metric = metricName || window.LAGOON_KLAVIYO_SURVEY_METRIC;
  const payload = Object.fromEntries(
    Object.entries(props || {}).filter(([k]) => !k.startsWith('$') && k !== 'Accepts Marketing')
  );
  try {
    const kl = window.klaviyo;
    if (kl) {
      // Newer API exposes .track(); older builds only take .push().
      if (typeof kl.track === 'function') { kl.track(metric, payload); return true; }
      if (typeof kl.push === 'function')  { kl.push(['track', metric, payload]); return true; }
    }
    if (window._learnq && typeof window._learnq.push === 'function') {
      window._learnq.push(['track', metric, payload]);
      return true;
    }
  } catch (e) {
    console.warn('[lagoon] onsite track failed', e);
  }
  return false;
};

// Whether Klaviyo can attribute activity to a known profile. Prefers the
// supported isIdentified() API (promise or callback) and falls back to the
// __kla_id cookie only when the SDK isn't present. Async because isIdentified()
// is — callers pass a callback.
window.lagoonIsKlaviyoIdentified = function (cb) {
  const done = function (v) { try { cb && cb(!!v); } catch (e) {} };
  const cookieFallback = function () {
    try { return document.cookie.indexOf('__kla_id') !== -1; } catch (e) { return false; }
  };
  try {
    const kl = window.klaviyo;
    if (kl && typeof kl.isIdentified === 'function') {
      const out = kl.isIdentified(done);
      if (out && typeof out.then === 'function') out.then(done).catch(function () { done(cookieFallback()); });
      return;
    }
  } catch (e) {}
  done(cookieFallback());
};
